import { CommonModule } from '@angular/common';
import { Component, Input, inject, input, output, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { StorageReference, getDownloadURL } from '@angular/fire/storage';
import { FormsModule } from '@angular/forms';
import { Dialog } from '@capacitor/dialog';
import { Platform } from '@ionic/angular/standalone';
import { TranslateService } from '@ngx-translate/core';
import { LoadingService } from '@scandium-oy/ngx-scandium';
import { parse } from 'date-fns';
import { combineLatest, of } from 'rxjs';
import { filter, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { AppCommonModule } from 'src/app/common.module';
import { Contractor } from 'src/app/models/contractor.model';
import { SiteArea } from 'src/app/models/site-area.model';
import { Site } from 'src/app/models/site.model';
import { RoomTicket, TicketStatus } from 'src/app/models/ticket.model';
import { AiService, AiTask } from 'src/app/services/ai.service';
import { AreasService } from 'src/app/services/areas.service';
import { ContractorsService } from 'src/app/services/contractors.service';
import { UsersService } from 'src/app/services/users.service';
import { WorkerTicketsService } from 'src/app/services/worker-tickets.service';
import { defaultTask, defaultTaskOnSave, isRoomTicket } from 'src/app/utility/ticket';
import { CarouselItem, CarouselItemComponent } from '../../carousel/item/carousel-item.component';
import { SelectSiteButtonComponent } from '../../select-site/select-site.component';
import { SpeechToTextComponent } from '../../speech-to-text/speech-to-text.component';
import { TakeVideoComponent } from '../../take-video/take-video.component';
import { TicketsCreatedComponent } from '../created/tickets-created.component';
import { NewAdhocComponent } from '../new/new-adhoc.component';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isExtendedAiTask(object: any): object is ExtendedAiTask {
  if (object == null) {
    return false;
  }
  return 'areaObj' in object;
}

interface ExtendedAiTask extends AiTask {
  guid?: string;
  areaObj?: SiteArea;
}

@Component({
  selector: 'app-ai-tasks',
  templateUrl: './ai.component.html',
  styleUrls: ['./ai.component.scss'],
  imports: [
    AppCommonModule,
    CommonModule,
    FormsModule,
    SpeechToTextComponent,
    SelectSiteButtonComponent,
    CarouselItemComponent,
    NewAdhocComponent,
    TakeVideoComponent,
    TicketsCreatedComponent,
  ],
})
export class AiTasksComponent {
  private aiService = inject(AiService);
  private areaService = inject(AreasService);
  private contractorsService = inject(ContractorsService);
  private loadingService = inject(LoadingService);
  private platform = inject(Platform);
  private translate = inject(TranslateService);
  private usersService = inject(UsersService);
  private workerTicketsService = inject(WorkerTicketsService);

  private videoUrl: string;

  siteS = signal<Site>(null);

  @Input()
  set site(value: Site) {
    this.siteS.set(value);
  }

  sites = input<Site[]>();
  saved = output<RoomTicket[]>();
  closed = output();
  hasSelectedTicket = output<boolean>();

  description = '';
  onlyOne = signal(true);
  loading = signal(false);
  progress = signal(0);

  residents = signal<CarouselItem<unknown, ExtendedAiTask | RoomTicket>[]>([]);
  found = signal<CarouselItem<unknown, ExtendedAiTask | RoomTicket>[]>([]);
  step = signal(1);
  selectedTicket = signal<RoomTicket>(null);
  selectedContractor = signal<Contractor>(null);

  invalidTickets = signal(true);

  areas$ = toObservable(this.siteS).pipe(
    filter((site) => site != null),
    switchMap((site) => this.areaService.getAreas(of(site))),
    shareReplay(1),
  );

  canSelectSubcontractor$ = combineLatest([toObservable(this.siteS), this.contractorsService.getCurrentContractor()]).pipe(
    filter(([site, contractor]) => site != null && contractor != null),
    map(([site, contractor]) => site.mainContractors?.some((it) => it.guid === contractor.guid)),
    shareReplay(1),
  );

  contractors$ = toObservable(this.siteS).pipe(
    filter((site) => site != null),
    switchMap((site) => this.contractorsService.getList({ site: site.guid })),
    map((contractors) => [...contractors.map((it) => ({ guid: it.guid, name: it.name })), { name: this.translate.instant('contractor.addNew'), guid: null }]),
    shareReplay(1),
  );

  users$ = this.contractorsService.getCurrentContractor().pipe(
    filter((contractor) => this.selectedContractor() != null || contractor != null),
    switchMap((contractor) => {
      const contractorGuid = this.selectedContractor()?.guid ?? contractor?.guid;
      return this.usersService.getList({ contractor: contractorGuid });
    }),
    shareReplay(1),
  );

  summary = signal<RoomTicket[]>([]);

  constructor() {
    this.platform.backButton.subscribeWithPriority(101, (processNextHandler) => {
      if (this.selectedTicket() != null) {
        this.selectedTicket.set(null);
      } else if (this.step() > 1) {
        this.step.update((val) => --val);
      } else {
        processNextHandler();
      }
    });
  }

  private startProgres() {
    return setInterval(() => {
      this.progress.update((val) => val += 0.01);
    }, 1000);
  }

  private updateTicketValidation() {
    const valid = this.found().every((it) => it.valid);
    this.invalidTickets.set(!valid);
  }

  private getRoomTicket(item: ExtendedAiTask, room: SiteArea) {
    const hours = 0;
    item.name = item.name === defaultTask ? defaultTaskOnSave : item.name;
    const images = [];
    const videos = this.videoUrl ? [this.videoUrl] : [];

    const history = [this.workerTicketsService.createHistory(TicketStatus.created, undefined, images, videos)];
    if (item.responsible === 'resident') {
      history.push(this.workerTicketsService.createHistory(TicketStatus.done, 'Vastuunjakotaulukon mukaisesti tehtävä kuuluu osakkaan vastuulle', images, videos, 'Kiira.app'));
    }
    if (item.contractor != null) {
      history.push(this.workerTicketsService.createHistory('assigned',
        'Vastuunjakotaulukon mukaisesti tehtävä kuuluu organisaation ' + item.contractor.name + ' vastuulle', images, videos, 'Kiira.app'));
    }

    const roomTicket: RoomTicket = {
      guid: null,
      name: item.name,
      plainName: item.name,
      hours,
      status: item.responsible === 'resident' ? TicketStatus.done : TicketStatus.created,
      history,
      users: [],
      usernames: [],
      area: room ? { guid: room.guid, name: room.name } : null,
      images,
      videos,
      attachments: [],
      description: item.description,
      deadline: this.getDeadline(item.deadline),
      contractor: item.contractor ?? null,
      creator: { guid: this.usersService.currentUserS().guid, name: this.usersService.currentUserS().displayName },
      schedule: null,
      location: null,
      site: this.siteS().guid,
      client: this.siteS().client,
    };
    if (roomTicket.contractor == null) {
      if (this.contractorsService.contractorS() != null) {
        roomTicket.contractor = { guid: this.contractorsService.contractorS().guid, name: this.contractorsService.contractorS().name };
      }
    }

    return roomTicket;
  }

  private getCarouselItems(roomTickets: RoomTicket[], parent: ExtendedAiTask | RoomTicket) {
    return roomTickets.map((it) => ({
      guid: it.guid ?? parent.guid,
      name: it.name,
      text: it.description,
      image: it.images?.length > 0 ? it.images[0] : null,
      preview$: this.areaService.getPreview(it),
      site: it.site,
      info: it.contractor?.name ?? '',
      hours: it.hours,
      date: it.deadline,
      parent,
      item: Object.assign({}, it, { guid: it.guid ?? parent.guid }),
      valid: true,
    }) as CarouselItem<ExtendedAiTask | RoomTicket, RoomTicket>);
  }

  private getRoom(areas: SiteArea[], item: ExtendedAiTask) {
    let parentArea: SiteArea;
    if (item.area) {
      const foundAreas = areas.map((area) => (
        { parent: area, area: this.areaService.findArea(item.area, area) })).filter((it) => it.area != null);
      if (foundAreas.length > 0) {
        parentArea = foundAreas[0].area;
        return parentArea;
      }
    }
    return null;
  }

  getDeadline(deadline: string | Date) {
    if (deadline) {
      if (typeof deadline === 'string') {
        return parse(deadline, 'dd.MM.yyyy', new Date());
      } else {
        return deadline;
      }
    }
    return null;
  }

  start() {
    this.step.update((val) => ++val);
  }

  deleteItem(index: number, residents = false) {
    if (residents) {
      const items = this.residents();
      items.splice(index, 1);
      this.residents.set(items);
      if (items.length === 0) {
        this.step.update((val) => --val);
      }
    } else {
      const items = this.found();
      items.splice(index, 1);
      this.found.set(items);
      if (items.length === 0) {
        this.step.update((val) => --val);
      }
    }
  }

  onItem(item: CarouselItem<unknown>) {
    const parent = item.item as RoomTicket | ExtendedAiTask;
    const area = isExtendedAiTask(parent) ? parent.areaObj : parent.area;
    const status = isExtendedAiTask(parent) && parent.responsible === 'resident' ? TicketStatus.done : TicketStatus.created;
    const users = [];
    const usernames = [];
    let images: string[] = [];
    let videos: string[] = [];
    if (this.videoUrl) {
      videos.push(this.videoUrl);
    }
    if (isRoomTicket(parent)) {
      if (parent.videos?.length > 0) {
        videos.push(...parent.videos);
      }
      if (parent.images?.length > 0) {
        images.push(...parent.images);
      }
    }

    const ticket: RoomTicket = {
      guid: item.guid,
      name: item.name,
      plainName: item.name,
      hours: item.hours ?? 0,
      description: item.text,
      status,
      history: [],
      images,
      videos,
      area: area ? { guid: area.guid, name: area.name } : null,
      deadline: this.getDeadline(parent.deadline),
      users,
      usernames,
      site: this.siteS().guid,
      client: this.siteS().client,
      contractor: parent.contractor ?? null,
    };
    this.selectedTicket.set(ticket);
    this.hasSelectedTicket.emit(true);
  }

  emptySelectedTicket() {
    this.selectedTicket.set(null);
    this.hasSelectedTicket.emit(false);
  }

  onMic(text: string) {
    this.description = this.description ? `${this.description} ${text}` : text;
  }

  onReference(reference: StorageReference) {
    if (reference) {
      this.aiService.transcript(reference.fullPath).pipe(
        take(1),
      ).subscribe({
        next: async (data) => {
          this.description = data.data;
          this.loadingService.hideLoading();
          this.videoUrl = await getDownloadURL(reference);
        }, error: (error) => {
          console.error(error);
          this.loadingService.hideLoading();
          Dialog.alert({ message: this.translate.instant('tickets.ai.error') });
        },
      });
    } else {
      this.loadingService.hideLoading();
    }
  }

  doTasks(text: string, areas: SiteArea[]) {
    this.loading.set(true);
    const timer = this.startProgres();
    this.aiService.doTasks(text, this.siteS().guid, this.onlyOne()).pipe(
      take(1),
    ).subscribe({
      next: (res: AiTask[]) => {
        this.loading.set(false);
        clearInterval(timer);
        const items = res.map((it) => {
          const area = this.getRoom(areas, it);
          return {
            guid: it.order,
            name: it.name,
            info: area?.name ?? null,
            info2: it.contractor?.name ?? null,
            text: it.description,
            item: Object.assign({}, it, { areaObj: area }) as ExtendedAiTask,
            hours: it.duration ? parseInt(it.duration) : 0,
            site: this.siteS().name,
            date: this.getDeadline(it.deadline),
            valid: true,
            video: this.videoUrl ?? null,
            contractor: it.contractor ?? null,
          };
        });
        const residents = items.filter((it) => it.item.responsible === 'resident');
        const other = items.filter((it) => it.item.responsible == null || it.item.responsible === 'buildingSociety');
        this.found.set(other);
        this.residents.set(residents);
        this.updateTicketValidation();
        this.step.update((val) => ++val);
      }, error: (error) => {
        console.error(error);
        this.loading.set(false);
        clearInterval(timer);
        Dialog.alert({ message: this.translate.instant('tickets.ai.error') });
      },
    });
  }

  onTicketSave(tickets: RoomTicket[]) {
    const current = this.selectedTicket();
    this.found.update((val) => {
      const index = val.findIndex((it) => it.guid === current.guid);
      const edited = val[index];
      edited.item.guid = edited.guid;
      const items = this.getCarouselItems(tickets, edited.item);
      val.splice(index, 1, ...items);
      return val;
    });
    this.updateTicketValidation();
    this.emptySelectedTicket();
  }

  onSite(site: Site) {
    this.siteS.set(site);
  }

  createTasks() {
    this.areaService.getMyAreas(this.siteS().guid).pipe(
      take(1),
    ).subscribe((areas) => {
      const items = [...this.found(), ...this.residents()];
      const tickets = items.map((it) => {
        if (isRoomTicket(it.item)) {
          return it.item;
        } else {
          const area = it.item.areaObj ?? areas[0];
          return this.getRoomTicket(it.item, area);
        }
      });
      this.summary.set(tickets);
      this.saved.emit(tickets);
      this.start();
    });
  }

  more() {
    this.description = '';
    this.found.set([]);
    this.residents.set([]);
    this.step.set(1);
  }

  end() {
    this.closed.emit();
  }
}
